import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { SCOPE_LABELS } from "@/lib/format";

const PHASE_NOTE = { ot: "Las OT se crearán en Gerencia (Fase 2).", warehouse: "Las bodegas se crearán en Abastecimiento (Fase 4)." };

export const UserRolesTab = ({ user, onSaved, canEdit }) => {
  const [roles, setRoles] = useState([]);
  const [sel, setSel] = useState(user.role_ids);
  useEffect(() => { api.get("/admin/roles").then((r) => setRoles(r.data)); }, []);
  const save = async () => {
    try { await api.put(`/admin/users/${user.id}/roles`, { role_ids: sel }); toast.success("Roles actualizados"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4" data-testid="user-roles-tab">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {roles.map((r) => (
          <label key={r.id} className={`flex gap-3 rounded-xl border p-4 cursor-pointer transition-colors ${sel.includes(r.id) ? "border-[#258F00] bg-[#f6fbf2]" : "border-[#e4e8e1] bg-white"} ${r.status !== "activo" ? "opacity-50" : ""}`}>
            <Checkbox data-testid={`user-role-checkbox-${r.code}`} disabled={!canEdit} checked={sel.includes(r.id)}
              onCheckedChange={(c) => setSel(c ? [...sel, r.id] : sel.filter((x) => x !== r.id))} />
            <div><div className="text-sm font-semibold">{r.name}</div><div className="text-xs text-[#5b6656]">{r.description}</div><div className="text-[11px] text-[#8a9584] mt-1">{r.permissions.length} privilegios</div></div>
          </label>
        ))}
      </div>
      {canEdit && <Button data-testid="save-user-roles-btn" className="rounded-full" onClick={save}>Guardar roles</Button>}
    </div>
  );
};

const ScopeBlock = ({ type, resources, value, onChange, disabled }) => {
  const all = value.includes("*");
  return (
    <div className="rounded-xl border border-[#e4e8e1] bg-white p-5" data-testid={`scope-block-${type}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="font-semibold text-sm">{SCOPE_LABELS[type]}</div>
        <label className="flex items-center gap-2 text-sm">Todos
          <Switch data-testid={`scope-all-switch-${type}`} disabled={disabled} checked={all} onCheckedChange={(c) => onChange(c ? ["*"] : [])} />
        </label>
      </div>
      {!all && (
        <div className="mt-3 grid sm:grid-cols-2 gap-2">
          {resources.length === 0 && <div className="text-xs text-[#5b6656] sm:col-span-2">{PHASE_NOTE[type] || "Sin recursos disponibles."}</div>}
          {resources.map((r) => (
            <label key={r.id} className="flex items-center gap-2 text-sm">
              <Checkbox data-testid={`scope-${type}-${r.id}`} disabled={disabled} checked={value.includes(r.id)}
                onCheckedChange={(c) => onChange(c ? [...value, r.id] : value.filter((x) => x !== r.id))} />{r.name}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

export const UserScopesTab = ({ user, canEdit, onSaved }) => {
  const [resources, setResources] = useState({ ot: [], warehouse: [], area: [] });
  const init = Object.fromEntries(Object.keys(SCOPE_LABELS).map((t) => [t, user.scopes.filter((s) => s.scope_type === t).map((s) => s.resource_id)]));
  const [val, setVal] = useState(init);
  useEffect(() => { api.get("/admin/users/scope-resources").then((r) => setResources(r.data)); }, []);
  const save = async () => {
    const scopes = Object.entries(val).flatMap(([t, ids]) => ids.map((id) => ({ scope_type: t, resource_id: id })));
    try { await api.put(`/admin/users/${user.id}/scopes`, { scopes }); toast.success("Alcances actualizados"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4" data-testid="user-scopes-tab">
      <p className="text-sm text-[#5b6656]">El alcance limita sobre qué recursos puede actuar el usuario, aunque tenga el privilegio. Se valida siempre en el servidor.</p>
      {Object.keys(SCOPE_LABELS).map((t) => <ScopeBlock key={t} type={t} resources={resources[t]} value={val[t]} disabled={!canEdit} onChange={(v) => setVal({ ...val, [t]: v })} />)}
      {canEdit && <Button data-testid="save-user-scopes-btn" className="rounded-full" onClick={save}>Guardar alcances</Button>}
    </div>
  );
};
